import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router'
import { Logo } from '@/components/brand/logo'
import { COMING_SOON, comingSoonProps } from '@/components/console/classes'
import { NAV_CONFIGURE, NAV_PRIMARY, type ConsoleNavItem } from '@/components/console/frame/nav'
import { useFrameIdentity } from '@/components/console/frame/use-frame-identity'
import { useDismiss } from '@/components/console/use-dismiss'
import { useNarrow } from '@/components/console/use-narrow'
import { Icon } from '@/components/icons/icon'
import { logout } from '@/lib/api/queries'
import { APP_NAME } from '@/lib/constants'
import { useConsoleRole } from '@/lib/demo/use-console-role'
import { cn } from '@/lib/utils'

interface SidebarProps {
  /** The mobile drawer is open (≤860px). */
  open: boolean
  onNavigate: () => void
}

/** The sidebar (`aside.side`): logo, workspace switcher, sections, help and profile. */
export function Sidebar({ open, onNavigate }: SidebarProps) {
  const role = useConsoleRole()
  const identity = useFrameIdentity()
  const narrow = useNarrow()

  const item = (nav: ConsoleNavItem) => {
    if (nav.ownerOnly && !role.canSeeBilling) return null
    const count = nav.attentionCount ? identity.attention : null
    return (
      <NavLink key={nav.to} to={nav.to} end={nav.end} className="nav-btn" onClick={onNavigate}>
        <Icon name={nav.icon} />
        <span className="nav-label">{nav.label}</span>
        {count ? (
          <span className="nav-count" title="Calls needing attention">
            {count}
          </span>
        ) : null}
      </NavLink>
    )
  }

  return (
    // Off screen on a phone until opened, so out of the tab order too.
    <aside className={cn('side', open && 'open')} id="side" aria-label="Main navigation" inert={narrow && !open}>
      <div className="side-head">
        <Link to="/dashboard" className="flex" aria-label={`${APP_NAME} · Overview`} onClick={onNavigate}>
          <Logo />
        </Link>
      </div>
      <WorkspaceSwitcher />
      <nav className="nav" aria-label="Sections">
        {NAV_PRIMARY.map(item)}
        <p className="cap">Configure</p>
        {NAV_CONFIGURE.map(item)}
      </nav>
      <div className="side-foot">
        <button type="button" {...comingSoonProps('help centre and live chat', 'nav-btn')}>
          <Icon name="help" />
          <span className="nav-label">Help &amp; support</span>
          <span className="hide"> · {COMING_SOON}</span>
        </button>
        <ProfileMenu onNavigate={onNavigate} />
      </div>
    </aside>
  )
}

function WorkspaceSwitcher() {
  const { workspace, workspaces } = useFrameIdentity()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, close, wrap, button)

  if (!workspace) return null
  return (
    <div className="ws" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="ws-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ws-ava" aria-hidden="true">
          {workspace.initials}
        </span>
        <span className="ws-label">
          <span className="ws-name">{workspace.name}</span>
          <span className="ws-plan">{workspace.sub}</span>
        </span>
        <Icon name="chev" style={{ width: 13, height: 13, marginLeft: 'auto', color: 'var(--faint)', flex: 'none' }} />
      </button>
      <div className="ws-menu" hidden={!open}>
        <p className="ws-head">Switch workspace</p>
        {workspaces.map((row) =>
          row.current ? (
            <button key={row.id} type="button" className="ws-row on" aria-current="true" onClick={close}>
              <span className="ws-ava">{row.initials}</span>
              <span className="ws-txt">
                <span className="ws-name">{row.name}</span>
                <span className="ws-sub">{row.sub}</span>
              </span>
              <Icon name="check" className="ws-check" />
            </button>
          ) : (
            <button key={row.id} type="button" {...comingSoonProps('switching workspaces', 'ws-row')}>
              <span
                className="ws-ava"
                style={row.warm ? { background: 'var(--acc-quiet)', color: 'var(--acc-text)' } : undefined}
              >
                {row.initials}
              </span>
              <span className="ws-txt">
                <span className="ws-name">{row.name}</span>
                <span className="ws-sub">{row.sub}</span>
              </span>
            </button>
          ),
        )}
        <button type="button" {...comingSoonProps('one sign-in for several businesses', 'ws-row ws-new')}>
          <span className="ws-plusic">
            <Icon name="plus" />
          </span>
          <span className="ws-name" style={{ fontWeight: 500 }}>
            Create workspace
          </span>
        </button>
      </div>
    </div>
  )
}

function ProfileMenu({ onNavigate }: { onNavigate: () => void }) {
  const { person } = useFrameIdentity()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, close, wrap, button)

  const signOut = useMutation({
    mutationFn: logout,
    // Clear every cached query, not just the session: the next person to sign in
    // on this browser must never see the previous one's data.
    onSuccess: async () => {
      queryClient.clear()
      await navigate('/login', { replace: true })
    },
  })

  if (!person) return null
  return (
    <div className="profwrap" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="profile"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Account menu, ${person.name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="ava" aria-hidden="true">
          {person.initials}
        </span>
        <span className="who">
          <b>{person.name}</b>
          <span>{person.roleLabel}</span>
        </span>
        <Icon name="chev" className="chev" />
      </button>
      <div className="profmenu" hidden={!open}>
        {person.email ? (
          <>
            <div className="profid">
              <p className="ws-head">Signed in as</p>
              {person.name !== person.email ? <b>{person.name}</b> : null}
              <span title={person.email}>{person.email}</span>
            </div>
            <div className="sep" />
          </>
        ) : null}
        <button
          type="button"
          onClick={() => {
            close()
            onNavigate()
            void navigate('/dashboard/settings')
          }}
        >
          <Icon name="user" />
          Profile settings
        </button>
        <button type="button" {...comingSoonProps('referrals')}>
          <Icon name="gift" />
          Refer a friend
        </button>
        <div className="sep" />
        <button type="button" {...comingSoonProps('stay signed in to more than one account')}>
          <Icon name="switch" />
          Sign in to another account
          <span className="newchip">{COMING_SOON}</span>
        </button>
        <button type="button" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
          <Icon name="logout" />
          {signOut.isPending ? 'Logging out…' : 'Log out'}
        </button>
      </div>
    </div>
  )
}
